import { partition } from 'lodash'
import { useMemo } from 'react'

import { getRuleViolationsMap } from '@bemlo/schedule'

import { useTranslations } from '../../../../translate'
import { byCellFilter } from '../by-filter'
import { Cell } from '../components/Cell'
import { CellShiftsColumn } from '../components/CellShiftsColumn'
import { DraftShifts } from '../components/DraftShifts'
import { NewShift } from '../components/NewShift'
import { ResourceTitle } from '../components/ResourceTitle'
import { Row } from '../components/Row'
import { ShiftError } from '../components/ShiftError'
import { ScheduleSchedulingRules } from '../types'
import { useEditShifts } from '../useEditShifts'
import { DraftShiftInput, useSchedule } from '../useSchedule'
import { getShiftsInWeek } from '../utils/get-shifts-in-week'

type FixedVacanciesProps = {
  rowKey: number
  scheduleResourceId: string | undefined
  schedulingRules?: ScheduleSchedulingRules
  draftShiftInput: Omit<DraftShiftInput, 'date' | 'scheduleSlotId'> | undefined
  onClickNewShiftType?: () => void
  multipleShifts?: boolean
}

export const FixedVacancies = (props: FixedVacanciesProps) => {
  const t = useTranslations()
  const { days, currentWeek, shifts: allShifts, shiftPopover } = useSchedule()

  const { enableEditing, onCreateDraftShift, onDeleteDraftShift } =
    useEditShifts(props.draftShiftInput)

  const [shifts, shiftsToDisplay, shiftsInWeek, dayErrors] = useMemo(() => {
    const shifts = allShifts.filter(byCellFilter(props))
    const shiftsToDisplay = shifts.filter(
      ({ bookedCandidateId }) => !bookedCandidateId,
    )
    const shiftsInWeek = currentWeek
      ? getShiftsInWeek(currentWeek, shifts)
      : shiftsToDisplay

    const dayErrors = props.schedulingRules
      ? getRuleViolationsMap(
          props.schedulingRules.ruleSets,
          shiftsToDisplay,
          props.schedulingRules.rules,
          props.schedulingRules.resource,
        )
      : {}

    return [shifts, shiftsToDisplay, shiftsInWeek, dayErrors]
  }, [allShifts, props, currentWeek])

  return (
    <Row>
      <Cell isTitle rowSpan={1}>
        <ResourceTitle
          title={t.shifts.row_n(props.rowKey + 1)}
          shifts={shiftsInWeek}
          bold={false}
        />
      </Cell>
      {days.map((day) => {
        const [draftShifts, shiftsOnDay] = partition(
          shiftsToDisplay.filter((shift) => shift.date === day.dateISO),
          ({ status }) => status === 'DRAFT',
        )
        const hasShifts = !!draftShifts.length || !!shiftsOnDay.length
        const errorsOnDay =
          dayErrors[day.dateISO]?.map((error) => ({
            error: t.$schedulingRuleViolation(error),
            isWarning: error.severity === 'warning',
          })) ?? []

        return (
          <Cell
            key={day.dateISO}
            disabled={!day.withinRange}
            weekend={day.isWeekend}
          >
            <CellShiftsColumn>
              {shiftsOnDay.map((shift) => {
                const isArchived = shift.status === 'ARCHIVED'
                const isUrgent = shift.isUrgent && !isArchived

                if (!shift) return null

                return (
                  <NewShift
                    key={shift.id}
                    shift={shift}
                    interestedCandidates={shift.interestedCandidates}
                    popoverContent={shiftPopover}
                    shiftIds={[shift.id]}
                    status={shift.status}
                    isUrgent={isUrgent}
                    isPublished={shift.isPublished}
                    isTendered={shift.isTendered}
                    unselectable={isArchived}
                    error={<ShiftError errors={errorsOnDay} />}
                  />
                )
              })}
              {enableEditing && day.withinRange && (
                <DraftShifts
                  showBadges
                  shifts={draftShifts}
                  onClickNewShift={
                    hasShifts && !props.multipleShifts
                      ? undefined
                      : props.draftShiftInput
                        ? () =>
                            onCreateDraftShift(
                              day.dateISO,
                              shifts[0]?.scheduleSlotId,
                            )
                        : props.onClickNewShiftType
                  }
                  onDeleteDraftShift={onDeleteDraftShift}
                />
              )}
            </CellShiftsColumn>
          </Cell>
        )
      })}
    </Row>
  )
}
